/**
 * سربرگِ «عکس‌ها» — قرینه‌ی apps/mobile/src/features/signals/SignalPhotos.tsx.
 *
 * تصمیمِ «برای من بازه یا نه» و ورق زدن این‌جا گرفته نمی‌شود؛ از
 * `core/lib/signalPhotos` می‌آید تا یک قاعده دو جا دو جور نشود. پشتوانه‌اش
 * هم RLS است نه این کد (مایگریشن 0141): اگر این فایل هم اشتباه بنویسد،
 * دیتابیس ردیف‌ها را نمی‌دهد.
 *
 * چهار کاری که می‌شود کرد، همان چهارتای موبایل:
 *   • نمای بزرگ با ورق زدن (این‌جا فلشِ صفحه‌کلید و دکمه‌ها، آن‌جا سوایپ)
 *   • دانلودِ یک عکس
 *   • انتخابِ چندتایی و دانلودِ همه با هم
 *   • رها کردنِ چند فایل روی صفحه برای فرستادن (معادلِ موبایلش انتخابِ
 *     چندتاییِ گالری است — گوشی کشیدن-و-رها کردن ندارد)
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { signalRepo } from '@/core/data/signalRepo';
import { photosTabState, stepPhoto } from '@/core/lib/signalPhotos';
import { t } from '@/core/lib/i18n';
import { reportError } from '@/core/lib/report';
import { useSession } from '@/core/session';
import type { SignalPhoto } from '@/core/domain/types';
import { filesToBase64, pickPhotoImages } from '@/features/profile/imagePicker';
import { downloadPhotos } from './photoDownload';
import { Card, Note } from '@/ui/kit';

export function SignalPhotos({ signalId, hasPlan, isCreator, myResponse, cancelled }: {
  signalId: string;
  hasPlan: boolean;
  isCreator: boolean;
  myResponse?: string | null;
  cancelled: boolean;
}) {
  const { profile } = useSession();
  const qc = useQueryClient();
  const [picking, setPicking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** `null` یعنی نمای بزرگ بسته است؛ عدد یعنی چندمین عکس باز است. */
  const [viewing, setViewing] = useState<number | null>(null);
  /** حالتِ انتخاب. `null` یعنی خاموش — نه «هیچ‌کدام انتخاب نشده». */
  const [selected, setSelected] = useState<Set<number> | null>(null);
  /** فایل بالای صفحه است و هنوز رها نشده — فقط برای نشان دادنِ قاب. */
  const [dragging, setDragging] = useState(false);

  const state = photosTabState({ hasPlan, isCreator, myResponse, cancelled });

  const photos = useQuery({
    queryKey: ['photos', signalId],
    queryFn: () => signalRepo.photos(signalId),
    enabled: state === 'open',
  });

  // عکسِ تازه‌ی بقیه بدونِ رفرش بیاید — همان اشتراکِ زنده‌ی گفتگو.
  useEffect(() => {
    if (state !== 'open') return undefined;
    return signalRepo.subscribePhotos(signalId, () => {
      qc.invalidateQueries({ queryKey: ['photos', signalId] });
    });
  }, [signalId, state, qc]);

  const remove = useMutation({
    mutationFn: (p: SignalPhoto) => signalRepo.removePhoto(p),
    onSuccess: () => {
      setViewing(null);
      qc.invalidateQueries({ queryKey: ['photos', signalId] });
    },
    onError: (e) => reportError('web.signal.removePhoto', e),
  });

  /**
   * فرستادنِ چند عکس. تا آپلود تمام نشود دکمه خاموش می‌ماند، وگرنه سه
   * کلیک سه نسخه می‌فرستد.
   */
  const upload = useCallback(async (base64s: string[]) => {
    if (!base64s.length) return;
    setPicking(true);
    try {
      const { failed } = await signalRepo.uploadPhotos(signalId, profile!.id, base64s);
      setError(failed ? t('suggestImageFailed') : null);
      qc.invalidateQueries({ queryKey: ['photos', signalId] });
    } catch (e) {
      reportError('web.signal.uploadPhoto', e);
      setError(t('suggestImageFailed'));
    } finally {
      setPicking(false);
    }
  }, [signalId, profile, qc]);

  const add = async () => {
    setPicking(true);
    try {
      await upload(await pickPhotoImages());
    } finally {
      setPicking(false);
    }
  };

  const download = async (list: SignalPhoto[]) => {
    if (!list.length) return;
    setSaving(true);
    try {
      const { failed } = await downloadPhotos(list);
      setError(failed ? t('photoDownloadFailed') : null);
      setSelected(null);
    } finally {
      setSaving(false);
    }
  };

  if (state === 'locked') return <Card><Note>{t('photosLockedNote')}</Note></Card>;
  if (state === 'members') return <Card><Note>{t('photosMembersNote')}</Note></Card>;

  const list = photos.data ?? [];
  const picked = list.filter((p) => selected?.has(p.id));

  const toggle = (id: number) => setSelected((prev) => {
    const next = new Set(prev ?? []);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  /**
   * رها کردنِ فایل روی این ناحیه = فرستادن.
   *
   * `dragOver` باید `preventDefault` بزند وگرنه مرورگر فایل را **باز
   * می‌کند** و کاربر از اپ بیرون می‌افتد — کلاسیک‌ترین اشتباهِ این کار.
   */
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const files = [...(e.dataTransfer?.files ?? [])];
    if (files.length) filesToBase64(files).then(upload);
  };

  return (
    <Card>
      <div
        className={`photo-drop${dragging ? ' over' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}>

        {selected ? (
          // نوارِ حالتِ انتخاب جای دکمه‌ی فرستادن را می‌گیرد، نه کنارش:
          // وسطِ انتخاب کردن، «عکس بفرست» کارِ کسی نیست.
          <div className="chiprow">
            <button type="button" className="btn fill"
              disabled={saving || !picked.length}
              onClick={() => download(picked)}>
              {saving ? '…' : t('photoDownloadN', { n: String(picked.length) })}
            </button>
            <button type="button" className="btn" onClick={() => setSelected(null)}>
              {t('photoSelectDone')}
            </button>
          </div>
        ) : (
          <div className="chiprow">
            <button type="button" className="btn fill" disabled={picking} onClick={add}>
              {picking ? '…' : t('photoAdd')}
            </button>
            {list.length ? (
              <button type="button" className="btn" onClick={() => setSelected(new Set())}>
                {t('photoSelect')}
              </button>
            ) : null}
          </div>
        )}

        <p className="dim photo-drop-hint">{t('photoDropHint')}</p>
        {error ? <Note tone="warn">{error}</Note> : null}

        {photos.isLoading ? <p className="dim">…</p> : null}
        {!photos.isLoading && list.length === 0 ? <p className="dim">{t('photosEmpty')}</p> : null}

        <div className="photo-grid">
          {list.map((p, i) => {
            const on = !!selected?.has(p.id);
            return (
              <figure key={p.id} className={`photo-cell${on ? ' on' : ''}`}>
                {/* کلیک روی عکس: در حالتِ عادی بازش می‌کند، در حالتِ انتخاب
                    تیکش می‌زند. دو کارِ متفاوت روی یک کلیک، ولی هیچ‌وقت
                    هم‌زمان — همان قرارِ هر گالری. */}
                <button type="button" className="photo-open"
                  aria-label={p.uploaderName ?? ''}
                  onClick={() => (selected ? toggle(p.id) : setViewing(i))}>
                  {/* آدرس امضا شده و عمر دارد (باکت private است)؛ اگر امضا
                      نگرفته باشد جای خالی می‌ماند، نه قابِ شکسته. */}
                  {p.url
                    ? <img src={p.url} alt="" loading="lazy" />
                    : <div className="photo-empty" />}
                  {on ? <span className="photo-tick" aria-hidden>✓</span> : null}
                </button>
                <figcaption>
                  <span className="dim">
                    {t('photoBy', { name: p.uploaderName ?? t('guest') })}
                  </span>
                </figcaption>
              </figure>
            );
          })}
        </div>
      </div>

      {viewing !== null && list[viewing] ? (
        <Lightbox
          photos={list}
          at={Math.min(viewing, list.length - 1)}
          saving={saving}
          canRemove={(p) => isCreator || p.uploaderId === profile?.id}
          onStep={(d) => setViewing((i) => stepPhoto(i ?? 0, d, list.length))}
          onDownload={(p) => download([p])}
          onRemove={(p) => remove.mutate(p)}
          onClose={() => setViewing(null)} />
      ) : null}
    </Card>
  );
}

/**
 * نمای بزرگ با ورق زدن.
 *
 * سه راهِ ورق زدن، چون سه جور آدم این صفحه را باز می‌کند: فلشِ صفحه‌کلید
 * (دسکتاپ)، دکمه‌های ‹ ›، و کشیدنِ افقی با انگشت (موبایلِ وب). `Escape`
 * می‌بندد — بی آن، تنها راهِ خروج پیدا کردنِ دکمه‌ی کوچکِ گوشه است.
 *
 * چرخشِ آخر-به-اول در `stepPhoto` است، نه این‌جا: همان تابعی که سوایپِ
 * موبایل هم از آن حساب می‌کند.
 */
function Lightbox({ photos, at, saving, canRemove, onStep, onDownload, onRemove, onClose }: {
  photos: SignalPhoto[];
  at: number;
  saving: boolean;
  canRemove: (p: SignalPhoto) => boolean;
  onStep: (delta: number) => void;
  onDownload: (p: SignalPhoto) => void;
  onRemove: (p: SignalPhoto) => void;
  onClose: () => void;
}) {
  const here = photos[at];
  /** جای شروعِ کشیدن با انگشت؛ `null` یعنی الان چیزی کشیده نمی‌شود. */
  const [touchX, setTouchX] = useState<number | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onStep(1);
      if (e.key === 'ArrowLeft') onStep(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onStep]);

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchX === null) return;
    const dx = e.changedTouches[0].clientX - touchX;
    setTouchX(null);
    // ۴۰ پیکسل: کمتر از آن، لمسِ ساده‌ی صفحه هم ورق می‌زد.
    if (Math.abs(dx) > 40) onStep(dx < 0 ? 1 : -1);
  };

  if (!here) return null;

  return (
    // کلیک روی زمینه می‌بندد؛ کلیک روی خودِ عکس و نوار نه (stopPropagation).
    <div className="lightbox" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="lightbox-stage"
        onTouchStart={(e) => setTouchX(e.touches[0].clientX)}
        onTouchEnd={onTouchEnd}
        onClick={(e) => e.stopPropagation()}>
        {photos.length > 1 ? (
          <button type="button" className="lightbox-nav prev"
            aria-label="‹" onClick={() => onStep(-1)}>‹</button>
        ) : null}
        {here.url ? <img src={here.url} alt="" /> : <div className="photo-empty" />}
        {photos.length > 1 ? (
          <button type="button" className="lightbox-nav next"
            aria-label="›" onClick={() => onStep(1)}>›</button>
        ) : null}
      </div>

      <div className="lightbox-bar" onClick={(e) => e.stopPropagation()}>
        <span className="dim">
          {t('photoCounter', { i: String(at + 1), n: String(photos.length) })}
          {here.uploaderName ? ` · ${t('photoBy', { name: here.uploaderName })}` : ''}
        </span>
        <span className="chiprow">
          <button type="button" className="btn fill" disabled={saving || !here.url}
            onClick={() => onDownload(here)}>
            {saving ? '…' : t('photoDownload')}
          </button>
          {canRemove(here) ? (
            <button type="button" className="btn" onClick={() => onRemove(here)}>
              {t('suggestImageRemove')}
            </button>
          ) : null}
          <button type="button" className="btn" onClick={onClose}>{t('filterClose')}</button>
        </span>
      </div>
    </div>
  );
}
